'use client';

import { getResourceData } from '@/api/public';
import logger from '@/utils/client-logger';
import type {
  PDCardCalibrationCardSamplingPolicy,
  PDCardCalibrationFaceGateLimits,
  PDCardCalibrationGateLimits,
} from '@zeelool/pd-card-calibration';
import type { FaceMeasurementMode } from './types';

const MEASUREMENT_SDK_CONFIG_TIMEOUT_MS = 2_000;
const MEASUREMENT_SDK_CONFIG_KEY = 'pd-card-calibration';

const FACE_GATE_LIMIT_KEYS = [
  'faceRollMaxDeg',
  'faceYawMaxDeg',
  'facePitchMaxDeg',
  'faceDistanceMinAreaRatio',
  'faceDistanceMaxAreaRatio',
] as const satisfies readonly (keyof PDCardCalibrationFaceGateLimits)[];

const CARD_GATE_LIMIT_KEYS = [
  'cardDistanceCloseMaxRatio',
  'cardDistanceFarMaxRatio',
  'cardRatioMaxError',
] as const satisfies readonly Exclude<
  keyof PDCardCalibrationGateLimits,
  keyof PDCardCalibrationFaceGateLimits
>[];

const ANGLE_GATE_LIMIT_KEYS = new Set<MeasurementSdkFaceGateLimitKey>([
  'faceRollMaxDeg',
  'faceYawMaxDeg',
  'facePitchMaxDeg',
]);

type MeasurementSdkFaceGateLimitKey = (typeof FACE_GATE_LIMIT_KEYS)[number];
type MeasurementSdkCardGateLimitKey = (typeof CARD_GATE_LIMIT_KEYS)[number];
type MeasurementSdkGateLimitKey = MeasurementSdkFaceGateLimitKey | MeasurementSdkCardGateLimitKey;

export type MeasurementSdkGateLimits = PDCardCalibrationGateLimits;

export interface MeasurementSdkRemoteConfig {
  gateLimits?: MeasurementSdkGateLimits;
  samplingPolicy?: PDCardCalibrationCardSamplingPolicy;
  cameraDistanceMultiplier?: number;
}

let cachedRemoteConfig: MeasurementSdkRemoteConfig | undefined;
let inFlightRemoteConfigPromise: Promise<MeasurementSdkRemoteConfig | undefined> | null = null;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function parseGateLimits(value: unknown): MeasurementSdkGateLimits | undefined {
  if (!isRecord(value)) return undefined;

  const gateLimits: Partial<Record<MeasurementSdkGateLimitKey, number>> = {};
  const gateLimitKeys: readonly MeasurementSdkGateLimitKey[] = [
    ...FACE_GATE_LIMIT_KEYS,
    ...CARD_GATE_LIMIT_KEYS,
  ];

  for (const key of gateLimitKeys) {
    if (!Object.prototype.hasOwnProperty.call(value, key)) return undefined;
    const limit = value[key];
    if (
      typeof limit !== 'number' ||
      !Number.isFinite(limit) ||
      limit <= 0 ||
      (ANGLE_GATE_LIMIT_KEYS.has(key as MeasurementSdkFaceGateLimitKey) && limit > 180)
    ) {
      return undefined;
    }
    gateLimits[key] = limit;
  }

  if (
    gateLimits.faceDistanceMinAreaRatio === undefined ||
    gateLimits.faceDistanceMaxAreaRatio === undefined ||
    gateLimits.faceDistanceMinAreaRatio >= gateLimits.faceDistanceMaxAreaRatio
  ) {
    return undefined;
  }

  return gateLimits as MeasurementSdkGateLimits;
}

function parseSamplingPolicy(value: unknown): PDCardCalibrationCardSamplingPolicy | undefined {
  if (!isRecord(value)) return undefined;

  const samplingPolicy: PDCardCalibrationCardSamplingPolicy = {};
  const samplingPolicyKeys = [
    'cardStabilityDetectionCount',
    'requiredSampleCount',
  ] as const satisfies readonly (keyof PDCardCalibrationCardSamplingPolicy)[];

  for (const key of samplingPolicyKeys) {
    if (!Object.prototype.hasOwnProperty.call(value, key)) continue;
    const count = value[key];
    if (typeof count !== 'number' || !Number.isFinite(count)) return undefined;
    samplingPolicy[key] = count;
  }

  return Object.keys(samplingPolicy).length ? samplingPolicy : undefined;
}

function parseCameraDistanceMultiplier(value: unknown): number | undefined {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 1 || value > 2) {
    return undefined;
  }
  return value;
}

function extractRemoteConfig(response: unknown): MeasurementSdkRemoteConfig | undefined {
  if (!isRecord(response) || response.code !== 200) {
    logger.warn('[FaceMeasurement] Failed to load SDK remote config', { response });
    return undefined;
  }

  const data = response.data;
  const jsonvalues = isRecord(data) ? data.jsonvalues : undefined;
  const config = isRecord(jsonvalues) ? jsonvalues[MEASUREMENT_SDK_CONFIG_KEY] : undefined;
  const gateLimits = parseGateLimits(isRecord(config) ? config.gateLimits : undefined);
  const samplingPolicy = parseSamplingPolicy(isRecord(config) ? config.samplingPolicy : undefined);
  const cameraDistanceMultiplier = parseCameraDistanceMultiplier(
    isRecord(config) ? config.cameraDistanceMultiplier : undefined,
  );

  if (!gateLimits && !samplingPolicy && cameraDistanceMultiplier === undefined) {
    logger.warn('[FaceMeasurement] Invalid SDK remote config', { config });
    return undefined;
  }

  return {
    ...(gateLimits ? { gateLimits } : {}),
    ...(samplingPolicy ? { samplingPolicy } : {}),
    ...(cameraDistanceMultiplier !== undefined ? { cameraDistanceMultiplier } : {}),
  };
}

function requestMeasurementSdkRemoteConfig(): Promise<MeasurementSdkRemoteConfig | undefined> {
  if (cachedRemoteConfig) return Promise.resolve(cachedRemoteConfig);
  if (inFlightRemoteConfigPromise) return inFlightRemoteConfigPromise;

  const request = Promise.resolve()
    .then(() =>
      getResourceData({
        jsonvalues: [MEASUREMENT_SDK_CONFIG_KEY],
      }),
    )
    .then((response: unknown) => {
      const remoteConfig = extractRemoteConfig(response);
      if (remoteConfig && !cachedRemoteConfig) cachedRemoteConfig = remoteConfig;
      return remoteConfig;
    })
    .catch((error: unknown) => {
      logger.warn('[FaceMeasurement] Failed to load SDK remote config', { error });
      return undefined;
    })
    .finally(() => {
      if (inFlightRemoteConfigPromise === request) inFlightRemoteConfigPromise = null;
    });

  inFlightRemoteConfigPromise = request;
  return request;
}

async function requestRemoteConfigWithinTimeout(): Promise<MeasurementSdkRemoteConfig | undefined> {
  const request = requestMeasurementSdkRemoteConfig();
  let timedOut = false;
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<undefined>((resolve) => {
    timeoutId = setTimeout(() => {
      timedOut = true;
      if (inFlightRemoteConfigPromise === request) inFlightRemoteConfigPromise = null;
      logger.warn('[FaceMeasurement] SDK remote config request timed out', {
        timeoutMs: MEASUREMENT_SDK_CONFIG_TIMEOUT_MS,
      });
      resolve(undefined);
    }, MEASUREMENT_SDK_CONFIG_TIMEOUT_MS);
  });

  const remoteConfig = await Promise.race([request, timeout]);
  if (!timedOut && timeoutId !== undefined) clearTimeout(timeoutId);
  return remoteConfig;
}

export async function loadMeasurementSdkRemoteConfig(
  mode: FaceMeasurementMode,
): Promise<MeasurementSdkRemoteConfig | undefined> {
  const remoteConfig = await requestRemoteConfigWithinTimeout();
  if (!remoteConfig) return undefined;

  if (mode === 'card') return remoteConfig;

  const { gateLimits, cameraDistanceMultiplier } = remoteConfig;
  if (!gateLimits && cameraDistanceMultiplier === undefined) return undefined;

  return {
    ...(gateLimits
      ? {
          gateLimits: Object.fromEntries(
            FACE_GATE_LIMIT_KEYS.map((key) => [key, gateLimits[key]]),
          ) as MeasurementSdkGateLimits,
        }
      : {}),
    ...(cameraDistanceMultiplier !== undefined ? { cameraDistanceMultiplier } : {}),
  };
}

export async function loadMeasurementSdkGateLimits(
  mode: FaceMeasurementMode,
): Promise<MeasurementSdkGateLimits | undefined> {
  return (await loadMeasurementSdkRemoteConfig(mode))?.gateLimits;
}
